// Client-side mirror of dprEngine.js's computeWorkingCapital, trimmed to
// what the wizard's live "Working Capital Assessment" preview needs. Kept
// here rather than imported from ca-backend since CRA's webpack build can't
// reach outside src/ — same pattern as dprLoanCalc.js.
//
// Reuses the ALREADY-mirrored RM Procurement and Sales Estimation preview
// calcs for the Current Assets build-up (Raw Material / Finished Goods
// stock, Sales Value, RM Cost) instead of re-deriving consumption/sales a
// third time — those two files' `qty`/`closingStockQty`/`unitCost` and
// `products[].closingStockQty`/`pricePerUnit` fields reproduce
// dprEngine.js's `cost`/`stockValue`/`fgStock` formulas exactly.
import { computeRmProcurementPreview } from './dprRmProcurementCalc';
import { computeSalesEstimatePreview } from './dprSalesEstimateCalc';

const round2 = n => Math.round((n + Number.EPSILON) * 100) / 100;

// Every branch exposes a normalized `mpbf`/`promoterMargin` pair (in
// addition to its own method-specific fields), matching dprEngine.js.
function computeMethodResult(method, wc, stock, debtors, creditors, sales, negotiatedLimit) {
  if (method === 'negotiated') {
    // Negotiated Limit Method: the flat, CA-decided "Working Capital Limit
    // Proposed" (means_of_finance.wcLimit) IS the Bank Finance figure every
    // year — not recomputed from a formula — with the promoter's margin as
    // whatever's left of Net WC Required (Stock+Debtors−Creditors) once
    // that limit is subtracted. Matches a real submitted DPR: the CC/OD
    // limit stays flat across all projected years while the underlying
    // stock/debtor build-up (and so the margin) keeps growing.
    const netWCRequired = round2(stock + debtors - creditors);
    const mpbf = negotiatedLimit;
    const promoterMargin = round2(Math.max(netWCRequired - negotiatedLimit, 0));
    return { netWCRequired, mpbf, promoterMargin };
  }
  if (method === 'turnover') {
    const t = wc.turnover || {};
    const pctOfTurnover = t.pctOfTurnover ?? 20;
    const totalWC = round2(sales * pctOfTurnover / 100);
    const marginPct = t.marginPct ?? 25;
    const margin = round2(totalWC * marginPct / 100);
    const bankFinance = round2(totalWC * (100 - marginPct) / 100);
    return { totalWC, margin, bankFinance, mpbf: bankFinance, promoterMargin: margin };
  }
  if (method === 'mpbf2' || method === 'mpbf3') {
    const currentAssets = round2(stock + debtors);
    const otherCL = creditors;
    const coreCA = method === 'mpbf3' ? round2(currentAssets * 0.25) : 0;
    const workingCA = round2(currentAssets - coreCA);
    const mpbf = Math.max(round2(0.75 * (workingCA - otherCL)), 0);
    return { currentAssets, otherCurrentLiabilities: otherCL, mpbf, promoterMargin: round2((workingCA - otherCL) - mpbf) };
  }
  const t = wc.traditional || {};
  const stockMarginPct = t.stockMarginPct ?? 25;
  const debtorMarginPct = t.debtorMarginPct ?? 40;
  const paidStock = round2(Math.max(stock - creditors, 0));
  const stockBankFinance = round2(paidStock * (100 - stockMarginPct) / 100);
  const debtorBankFinance = round2(debtors * (100 - debtorMarginPct) / 100);
  const mpbf = round2(stockBankFinance + debtorBankFinance);
  const promoterMargin = round2((paidStock + debtors) - mpbf);
  return { paidStock, stockBankFinance, debtorBankFinance, mpbf, promoterMargin };
}

// Returns { method, byYear: [{ year, debtors, creditors, stock, rmStock,
// fgStock, mpbf, promoterMargin, ...method-specific fields }] } — same
// shape as dprEngine.js's computeWorkingCapital.
export function computeWorkingCapitalPreview(caseData) {
  const wc = caseData.wc_config || {};
  const method = wc.method || 'traditional';
  const creditorDays = wc.creditorDays ?? 15;
  const debtorDays = wc.debtorDays ?? 40;

  const negotiatedLimit = round2(Number((caseData.means_of_finance || {}).wcLimit) || 0);
  const rmByYear = computeRmProcurementPreview(caseData);
  const salesByYear = computeSalesEstimatePreview(caseData);

  return {
    method,
    byYear: rmByYear.map((rmRow, idx) => {
      const salesRow = salesByYear[idx];
      const sales = salesRow.totalSalesValue;
      const rmCost = round2(rmRow.lines.reduce((s, l) => s + l.qty * l.unitCost, 0));
      const rmStock = round2(rmRow.lines.reduce((s, l) => s + l.closingStockQty * l.unitCost, 0));
      const fgStock = round2(salesRow.products.reduce((s, p) => s + p.closingStockQty * p.pricePerUnit, 0));
      const stock = round2(rmStock + fgStock);
      const debtors = round2(sales * debtorDays / 300);
      const creditors = round2(rmCost * creditorDays / 300);

      return {
        year: rmRow.year, debtors, creditors, stock, rmStock, fgStock, method,
        ...computeMethodResult(method, wc, stock, debtors, creditors, sales, negotiatedLimit),
      };
    }),
  };
}
